import { 
  Vulnerability, 
  VulnerabilityFilters, 
  VulnerabilityStats,
  Severity,
  KaiStatus 
} from '../types/vulnerability';

/**
 * Processes and filters vulnerability data efficiently
 */
export class VulnerabilityDataProcessor {
  /**
   * Filter vulnerabilities based on criteria
   */
  static filter(
    vulnerabilities: Vulnerability[], 
    filters: VulnerabilityFilters
  ): Vulnerability[] {
    return vulnerabilities.filter(vuln => {
      // Filter by kaiStatus exclusion
      if (filters.excludeInvalidNorisk && vuln.kaiStatus === 'invalid - norisk') {
        return false;
      }
      
      if (filters.excludeAiInvalidNorisk && vuln.kaiStatus === 'ai-invalid-norisk') {
        return false;
      }

      // Filter by kaiStatus inclusion
      if (filters.kaiStatus && filters.kaiStatus.length > 0) {
        if (!vuln.kaiStatus || !filters.kaiStatus.includes(vuln.kaiStatus)) {
          return false;
        }
      }

      // Filter by severity
      if (filters.severity && filters.severity.length > 0) {
        if (!vuln.severity || !filters.severity.includes(vuln.severity)) {
          return false;
        }
      }

      // Filter by package name
      if (filters.packageName) {
        const packageName = vuln.packageName?.toLowerCase() || '';
        if (!packageName.includes(filters.packageName.toLowerCase())) {
          return false;
        }
      }

      // Filter by search query (searches in multiple fields)
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const searchableFields = [
          vuln.cveId,
          vuln.packageName,
          vuln.packageVersion,
          vuln.description,
          ...(vuln.riskFactors?.map(rf => `${rf.name}:${rf.value}`) || []),
        ].filter(Boolean).join(' ').toLowerCase();

        if (!searchableFields.includes(query)) {
          return false;
        }
      }

      // Filter by date range
      if (filters.dateRange) {
        const vulnDate = vuln.publishedDate || vuln.modifiedDate;
        if (vulnDate) {
          if (filters.dateRange.start && vulnDate < filters.dateRange.start) {
            return false;
          }
          if (filters.dateRange.end && vulnDate > filters.dateRange.end) {
            return false;
          }
        }
      }

      return true;
    });
  }

  /**
   * Sort vulnerabilities
   */
  static sort(
    vulnerabilities: Vulnerability[],
    sortBy: 'id' | 'severity' | 'date' | 'package' | 'score' = 'severity',
    order: 'asc' | 'desc' = 'desc'
  ): Vulnerability[] {
    const sorted = [...vulnerabilities];

    const severityOrder: Record<Severity, number> = {
      critical: 5,
      high: 4,
      medium: 3,
      low: 2,
      info: 1,
      unknown: 0,
    };

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'id': {
          const idA = (a.cveId || a.id || '').toLowerCase();
          const idB = (b.cveId || b.id || '').toLowerCase();
          comparison = idA.localeCompare(idB);
          break;
        }

        case 'severity':
          comparison = (severityOrder[a.severity || 'unknown'] || 0) -
            (severityOrder[b.severity || 'unknown'] || 0);
          break;

        case 'date': {
          const dateA = new Date(a.publishedDate || a.modifiedDate || 0).getTime();
          const dateB = new Date(b.publishedDate || b.modifiedDate || 0).getTime();
          comparison = dateA - dateB;
          break;
        }

        case 'package': {
          const packageA = (a.packageName || '').toLowerCase();
          const packageB = (b.packageName || '').toLowerCase();
          comparison = packageA.localeCompare(packageB);
          break;
        }

        case 'score': {
          comparison = (a.score || a.cvss?.baseScore || 0) -
            (b.score || b.cvss?.baseScore || 0);
          break;
        }
      }

      return order === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }

  /**
   * Calculate statistics from vulnerabilities
   */
  static calculateStats(vulnerabilities: Vulnerability[]): VulnerabilityStats {
    const stats: VulnerabilityStats = {
      total: vulnerabilities.length,
      bySeverity: {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0,
        info: 0,
        unknown: 0,
      },
      byKaiStatus: {
        'invalid - norisk': 0,
        'ai-invalid-norisk': 0,
        'valid': 0,
        'ai-valid': 0,
        'pending': 0,
        'unknown': 0,
      },
      riskFactorByName: {},
      riskFactorTop: [],
      trendByMonth: [],
      analysisRelationship: [],
      criticalCount: 0,
      highCount: 0,
      mediumCount: 0,
      lowCount: 0,
      filteredOutInvalid: 0,
      filteredOutAiInvalid: 0,
      filteredPercentage: 100,
      topCritical: [],
    };

    const severityRank: Record<Severity, number> = {
      critical: 5,
      high: 4,
      medium: 3,
      low: 2,
      info: 1,
      unknown: 0,
    };

    const riskFactorByName: Record<string, number> = {};
    const trendMap = new Map<string, { total: number; critical: number; high: number; aiInvalid: number }>();
    const criticalCandidates: Vulnerability[] = [];

    vulnerabilities.forEach(vuln => {
      // Count by severity
      const severity = vuln.severity || 'unknown';
      stats.bySeverity[severity]++;
      
      if (severity === 'critical') stats.criticalCount++;
      if (severity === 'high') stats.highCount++;
      if (severity === 'medium') stats.mediumCount++;
      if (severity === 'low') stats.lowCount++;

      // Count by kaiStatus
      const kaiStatus = vuln.kaiStatus || 'unknown';
      if (kaiStatus in stats.byKaiStatus) {
        stats.byKaiStatus[kaiStatus as KaiStatus]++;
      }

      // Count risk factors
      if (vuln.riskFactors) {
        vuln.riskFactors.forEach(rf => {
          const key = rf.name?.trim() || 'Unknown';
          riskFactorByName[key] = (riskFactorByName[key] || 0) + 1;
        });
      }

      // Collect trend data by month (YYYY-MM)
      const isoDate = vuln.publishedDate || vuln.modifiedDate;
      if (isoDate) {
        const date = new Date(isoDate);
        if (!Number.isNaN(date.getTime())) {
          const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          if (!trendMap.has(period)) {
            trendMap.set(period, { total: 0, critical: 0, high: 0, aiInvalid: 0 });
          }
          const current = trendMap.get(period)!;
          current.total += 1;
          if (severity === 'critical') {
            current.critical += 1;
          }
          if (severity === 'high') {
            current.high += 1;
          }
          if (kaiStatus === 'ai-invalid-norisk') {
            current.aiInvalid += 1;
          }
        }
      }

      if (severity === 'critical' || severity === 'high') {
        criticalCandidates.push(vuln);
      }
    });

    stats.riskFactorByName = riskFactorByName;
    stats.riskFactorTop = Object.entries(riskFactorByName)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    stats.trendByMonth = Array.from(trendMap.entries())
      .map(([period, data]) => ({ period, ...data }))
      .sort((a, b) => a.period.localeCompare(b.period))
      .slice(-18);

    stats.analysisRelationship = [
      {
        status: 'Valid',
        manual: stats.byKaiStatus['valid'],
        ai: stats.byKaiStatus['ai-valid'],
      },
      {
        status: 'Invalid - No Risk',
        manual: stats.byKaiStatus['invalid - norisk'],
        ai: stats.byKaiStatus['ai-invalid-norisk'],
      },
      {
        status: 'Pending',
        manual: stats.byKaiStatus['pending'],
        ai: 0,
      },
      {
        status: 'Unknown',
        manual: stats.byKaiStatus['unknown'],
        ai: 0,
      },
    ];

    stats.filteredOutInvalid = stats.byKaiStatus['invalid - norisk'];
    stats.filteredOutAiInvalid = stats.byKaiStatus['ai-invalid-norisk'];

    stats.topCritical = criticalCandidates
      .sort((a, b) => {
        const severityDiff = (severityRank[b.severity || 'unknown'] || 0) -
          (severityRank[a.severity || 'unknown'] || 0);
        if (severityDiff !== 0) {
          return severityDiff;
        }
        const scoreA = a.score || a.cvss?.baseScore || 0;
        const scoreB = b.score || b.cvss?.baseScore || 0;
        return scoreB - scoreA;
      })
      .slice(0, 5);

    return stats;
  }

  /**
   * Get unique values for a field (useful for filter dropdowns)
   */
  static getUniqueValues<T extends keyof Vulnerability>(
    vulnerabilities: Vulnerability[],
    field: T
  ): Array<Vulnerability[T]> {
    const values = new Set<Vulnerability[T]>();
    vulnerabilities.forEach(vuln => {
      if (vuln[field] !== undefined && vuln[field] !== null) {
        values.add(vuln[field]);
      }
    });
    return Array.from(values);
  }

  /**
   * Paginate vulnerabilities
   */
  static paginate<T>(
    items: T[],
    page: number,
    pageSize: number
  ): { items: T[]; totalPages: number; totalItems: number } {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    const paginatedItems = items.slice(start, end);
    const totalPages = Math.ceil(items.length / pageSize);

    return {
      items: paginatedItems,
      totalPages,
      totalItems: items.length,
    };
  }
}

